import { registerCustomMessageMagicNumber } from '@m1/mavlink';
import { useContext, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import * as sheco from './messages';
import useCustomTelemetryHandler from './useCustomTelemetryHandler';

import { MessageContext } from '@/helpers/MessageProvider';
import useMavLinkCommandHandler from '@/hooks/useMavLinkCommandHandler';
import useMavLinkTelemetryHandler from '@/hooks/useMavLinkTelemetryHandler';
import usePingListener from '@/hooks/usePingListener';
import useWaitingRobot from '@/hooks/useWaitingRobot';
import useWaitingRobotTimer from '@/hooks/useWaitingRobotTimer';
import EventEmitter from '@/libs/EventEmitter';

const useOrganizer = (robot) => {
  const { publishCommand } = useContext(MessageContext);
  const address = useSelector((state) => state.telemetry[robot.id]?.address);
  const stickControl = useRef({ y: 0, z: 0 }); // 수동 제어값

  usePingListener(robot.id);
  useWaitingRobotTimer(robot.id);
  const isWaiting = useWaitingRobot(robot.id);

  useCustomTelemetryHandler(robot);
  useMavLinkTelemetryHandler(robot);
  const { addInterval, clearIntervals } = useMavLinkCommandHandler(robot);

  const [sysId, compId] = useMemo(() => {
    return [address?.sysId, address?.compId];
  }, [address]);

  useEffect(() => {
    if (!robot.isActive) return;
    if (isWaiting) return;
    if (!sysId) return;

    // MavLink custom messages 정의
    try {
      registerCustomMessageMagicNumber('1101', sheco.UsvData.MAGIC_NUMBER);
      registerCustomMessageMagicNumber('1102', sheco.UsvEmergencyGcsData.MAGIC_NUMBER);
      registerCustomMessageMagicNumber('1104', sheco.UsvOilactGcsData.MAGIC_NUMBER);
    } catch (e) {
      // TODO
    }

    let subscribeToken;
    if (robot.isOwned) {
      publishCommand(robot, 'request/autopilot_version', [[sysId, compId]]);
      addInterval('action/stick_control', () => [[sysId, 0, stickControl.current.y, stickControl.current.z, 0]], 50);

      subscribeToken = EventEmitter.subscribe(`${robot.id}/set/stickControl`, ({ y, z }) => {
        stickControl.current.y = y;
        stickControl.current.z = z;
      });
    }

    addInterval('request/heartbeat', [[]], 1000);
    addInterval('request/message_interval', [[sysId, compId, 30, 500000]], 3000); // ATTITUDE
    addInterval('request/message_interval', [[sysId, compId, 31, 500000]], 3000); // ATTITUDE_QUATERNION
    addInterval('request/message_interval', [[sysId, compId, 32, 500000]], 3000); // LOCAL_POSITION_NED
    addInterval('request/message_interval', [[sysId, compId, 33, 500000]], 3000); // GLOBAL_POSITION_INT
    addInterval('request/message_interval', [[sysId, compId, 36, 500000]], 3000); // SERVO_OUTPUT_RAW

    return () => {
      if (subscribeToken) {
        EventEmitter.unsubscribe(subscribeToken);
      }
      clearIntervals();
    };
  }, [robot.isActive, isWaiting, sysId]);
};

export default useOrganizer;
